// src/components/chat/StreamingChatPanel.tsx

import * as React from "react";
import { useRef, useState, useEffect, useCallback } from "react";

// Import hooks
import { useChatAnalysis } from "./hooks/useChatAnalysis";
import { useDocumentInsertion } from "./hooks/useDocumentInsertion";
import { useAnimations } from "./hooks/useAnimations";
import { useResizeObserver } from "./hooks/useResizeObserver";
import { useScrollPositionPreservation } from "./hooks/useScrollPositionPreservation";
import { useFileUpload } from "../../hooks/useFileUpload";
import { UseFileUploadReturn } from "../../hooks/useFileUpload";

// Import styles
import { modernChatStyles } from "./styles/modernChatStyles";
import { fileUploadAnimations } from "./styles/fileUploadStyles";

// Import components
import ModernChatInput, { ModernChatInputRef } from "./ModernChatInput";
import ChatMessages from "./ChatMessages";
import ErrorMessage from "./ErrorMessage";
import FileUploadCarousel from "./FileUploadCarousel";
import QuickStartButtons, { isTier3CapableAgent } from "./QuickStartButtons";
import { DRAFT_CERTIFICATE_PROMPT } from "./draftCertificate/DraftCertificateAction";
import { UserMessageAction } from "../../interfaces/chatSession";
import { useEntitlements } from "../../hooks/useEntitlements";
import { createLogger } from "../../utils/logger";
// Import agent context to detect agent changes
import { useAgent } from "../../contexts/AgentContext";
import { useAuth } from "../../contexts/AuthContext";
import { parseFirstName } from "../../helpers/nameUtils";

// Import agent model service factory to access file methods
import { agentModelServiceFactory } from "../../services/agentModelServiceFactory";
import { AgentAwareModelService } from "../../services/agentAwareModelService";

// Create a logger instance for this component
const log = createLogger("StreamingChatPanel");

interface StreamingChatPanelProps {
  fileUpload?: UseFileUploadReturn; // Receive file upload state from parent
}

const StreamingChatPanel: React.FC<StreamingChatPanelProps> = ({
  fileUpload: externalFileUpload,
}) => {
  // Set up CSS animations (now includes file upload animations)
  useAnimations();

  // Add file upload animations to the page
  React.useEffect(() => {
    const styleId = "file-upload-animations";
    if (!document.getElementById(styleId)) {
      const styleSheet = document.createElement("style");
      styleSheet.id = styleId;
      styleSheet.innerHTML = fileUploadAnimations;
      document.head.appendChild(styleSheet);

      return () => {
        const element = document.getElementById(styleId);
        if (element) {
          document.head.removeChild(element);
        }
      };
    }
  }, []);

  // Track container dimensions
  const { ref: containerRef, height: containerHeight } = useResizeObserver<HTMLDivElement>();

  // Ref for ModernChatInput to enable programmatic control (defined early for use in callback)
  const chatInputRef = useRef<ModernChatInputRef>(null);

  // State to hold prompt that needs to be restored after layout transition settles
  const [pendingPromptRestore, setPendingPromptRestore] = useState<string | null>(null);

  // Callback to restore user prompt when generation is cancelled before any content appears
  // Instead of directly setting the input, we store the prompt and let a useEffect handle it
  // This ensures the value is set on the correct input after any layout transitions complete
  const handlePromptRestore = useCallback((prompt: string) => {
    setPendingPromptRestore(prompt);
  }, []);

  // Effect to restore the pending prompt when the layout has settled
  // This handles the case where the layout transitions (centered → bottom → centered)
  // after clicking STOP, which would otherwise lose the value
  useEffect(() => {
    if (pendingPromptRestore && chatInputRef.current) {
      chatInputRef.current.setInputValue(pendingPromptRestore);
      chatInputRef.current.focusInput();
      setPendingPromptRestore(null); // Clear after successful restore
    }
  }, [pendingPromptRestore, chatInputRef.current]);

  // Use custom hooks
  const chatAnalysis = useChatAnalysis({ onPromptRestore: handlePromptRestore });
  const documentInsertion = useDocumentInsertion();

  // Get current agent to detect changes
  const { currentAgent, currentAgentFeatures } = useAgent();
  // Get user information for personalized welcome message
  const { userInfo } = useAuth();
  // Previous agent tracking is now handled at App level to prevent issues with tab switching

  // Cache greeting for 2 minutes to prevent random changes during usage
  const [cachedGreeting, setCachedGreeting] = useState<{
    message: string;
    timestamp: number;
  } | null>(null);

  // Animation state for smooth transitions
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [showCenteredLayout, setShowCenteredLayout] = useState(true);
  const prevMessageCountRef = useRef(0);

  // Generate personalized greeting based on time and random selection
  const generateGreeting = (name?: string): string => {
    const firstName = parseFirstName(name);
    const hour = new Date().getHours();

    // Define available greetings - EASILY EXTENDABLE
    const greetings: string[] = [];

    // Time-based greetings (12:00 AM - 11:59 AM)
    if (hour >= 0 && hour < 12) {
      greetings.push(`Good morning, ${firstName}`);
    }

    // Time-based greetings (12:00 PM - 5:59 PM)
    if (hour >= 12 && hour < 18) {
      greetings.push(`Good afternoon, ${firstName}`);
    }

    // Time-based greetings (6:00 PM - 11:59 PM)
    if (hour >= 18 && hour < 24) {
      greetings.push(`Good evening, ${firstName}`);
    }

    // Always available greetings (shown regardless of time)
    if (firstName) {
      // Simple welcomes
      greetings.push(`Hello, ${firstName}`);
      greetings.push(`Welcome, ${firstName}`);
      greetings.push(`Great to see you, ${firstName}. Let's get to work`);
      greetings.push(`Glad you're here, ${firstName}`);

      // Contract humor (light & professional)
      greetings.push(`Another day, another MSA to master, ${firstName}`);
      greetings.push(`Time to make those terms favorable, ${firstName}`);
      greetings.push(`Let's find what they hoped you'd miss, ${firstName}`);
      greetings.push(`Best duo in contracting? You and me, ${firstName}`);
    }

    // Fallback if no name available
    if (greetings.length === 0) {
      return "Hello!";
    }

    // Randomly select a greeting
    const randomIndex = Math.floor(Math.random() * greetings.length);
    return greetings[randomIndex];
  };

  // Get greeting with 2-minute cache
  const getPersonalizedGreeting = (name?: string): string => {
    const CACHE_DURATION_MS = 2 * 60 * 1000; // 2 minutes
    const now = Date.now();

    // Check if we have a valid cached greeting
    if (cachedGreeting && now - cachedGreeting.timestamp < CACHE_DURATION_MS) {
      return cachedGreeting.message;
    }

    // Generate new greeting and cache it
    const newGreeting = generateGreeting(name);
    setCachedGreeting({
      message: newGreeting,
      timestamp: now,
    });

    return newGreeting;
  };

  // Clear cache when user info changes (e.g., different user logs in)
  useEffect(() => {
    setCachedGreeting(null);
  }, [userInfo?.name]);

  // Handle smooth transition when first message is sent
  useEffect(() => {
    const currentMessageCount = chatAnalysis.currentSession.messages.length;
    const hadNoMessages = prevMessageCountRef.current === 0;
    const hasMessages = currentMessageCount > 0;

    // First message sent - trigger transition from centered to bottom
    if (hadNoMessages && hasMessages) {
      setIsTransitioning(true);
      // Wait for fade animation, then switch layout
      setTimeout(() => {
        setShowCenteredLayout(false);
        setIsTransitioning(false);
      }, 200); // Simple fade duration
    }

    // Messages cleared - return to centered layout
    if (!hadNoMessages && !hasMessages) {
      setShowCenteredLayout(true);
    }

    prevMessageCountRef.current = currentMessageCount;
  }, [chatAnalysis.currentSession.messages.length]);

  // Use provided fileUpload from parent or create fallback for backward compatibility
  const fileUpload =
    externalFileUpload ||
    useFileUpload(
      (files) => {
        // Fallback behavior if no fileUpload provided from parent
        if (currentAgentFeatures.allowFileUpload) {
          const modelService = agentModelServiceFactory.getServiceForAgent(currentAgent.id);
          if (modelService && "setAdditionalFiles" in modelService) {
            (modelService as AgentAwareModelService).setAdditionalFiles(files);
            log.info("Updated model service with uploaded files (fallback)", {
              fileCount: files.length,
              fileNames: files.map((f) => f.name),
              agentId: currentAgent.id,
            });
          }
        }
      },
      {
        // Pass agent-specific validation configuration
        supportedFileTypes: currentAgentFeatures.supportedFileTypes,
        maxFileSizeMB: currentAgentFeatures.maxFileSizeMB,
        maxFileCount: currentAgentFeatures.maxFileCount,
      }
    );

  // Selected text tracking state
  const [messageSelectedText, setMessageSelectedText] = React.useState<Map<string, string>>(
    new Map()
  );
  const [pendingSelectedText, setPendingSelectedText] = React.useState<string>("");

  // Message count tracking for precise timing
  const previousMessageCountRef = useRef<number>(0);
  const previousAgentRef = useRef(currentAgent.id);
  const isPendingAssociationRef = useRef<boolean>(false);

  // Ref for the scrollable chat container (used by CitationTooltipProvider for scroll-based tooltip dismissal)
  const chatContainerRef = useRef<HTMLDivElement>(null);

  // Preserve scroll position per agent across agent switches and view transitions
  useScrollPositionPreservation(currentAgent.id, chatContainerRef);

  // Effect to track agent changes (conversation preservation is now handled by multi-agent sessions)
  React.useEffect(() => {
    // Check if the agent has actually changed (not just initial mount)
    if (previousAgentRef.current && currentAgent.id !== previousAgentRef.current) {
      log.info("Agent changed in chat panel, preserving conversation", {
        from: previousAgentRef.current,
        to: currentAgent.id,
        note: "Conversation preserved via multi-agent session management",
      });

      // Each agent maintains its own session independently
      // Only clear message-text mappings as they're UI-specific
      setMessageSelectedText(new Map());
      setPendingSelectedText("");
    }

    // Update our tracking reference for the next change
    previousAgentRef.current = currentAgent.id;
  }, [currentAgent.id]);

  // Combine errors from all sources
  const error = chatAnalysis.error || documentInsertion.error || fileUpload.error;

  // Handle error dismissal
  const handleDismissError = () => {
    // Clear errors from all sources
    chatAnalysis.setError(null);
    documentInsertion.setError(null);
    fileUpload.clearError();
  };

  // State to track if clear button is visible - always show the new menu buttons
  const [showClearButton, setShowClearButton] = useState(true);

  // Always show the new menu buttons
  useEffect(() => {
    setShowClearButton(true);
  }, []);

  // Handle insertion of response into document
  const handleInsert = async () => {
    log.info("Insert button clicked", {
      hasResponse: !!(chatAnalysis.lastResponseText || chatAnalysis.directResponseText),
    });

    // Use either the session state or direct state for the response
    const responseToInsert = chatAnalysis.lastResponseText || chatAnalysis.directResponseText;

    // Insert the response
    await documentInsertion.insertResponseIntoDocument(responseToInsert, true);
  };

  /**
   * Enhanced message sending with precise selected text tracking
   * Now also ensures files are synced with the model service before sending
   */
  const handleSendMessage = (message: string, opts?: { action: UserMessageAction }) => {
    if (message.trim()) {
      // Clear any file upload errors when user sends a message
      if (currentAgentFeatures.allowFileUpload) {
        fileUpload.clearError();
      }

      // Ensure files are synced with model service before sending
      if (currentAgentFeatures.allowFileUpload && fileUpload.files.length > 0) {
        const modelService = agentModelServiceFactory.getServiceForAgent(currentAgent.id);
        if (modelService && "setAdditionalFiles" in modelService) {
          const files = fileUpload.files.map((f) => f.file);
          (modelService as AgentAwareModelService).setAdditionalFiles(files);
          log.info("Synced files with model service before sending message", {
            fileCount: files.length,
            agentId: currentAgent.id,
          });
        }
      }

      // Capture current state before any async operations
      const currentMessageCount = chatAnalysis.currentSession.messages.length;

      log.debug("Starting message send process", {
        message: message.substring(0, 50) + "...",
        currentMessageCount,
        hasPendingText: !!pendingSelectedText,
        filesAttached: fileUpload.files.length,
      });

      // TEMPORARY: Selected text feature disabled for this release
      // TODO: Re-enable by uncommenting the getSelectedText() call below
      /*
      // Get selected text for UI display (separate from document context)
      getSelectedText()
        .then((selectedTextForUI) => {
          log.debug("Retrieved selected text for UI", {
            length: selectedTextForUI.length,
            preview: selectedTextForUI.substring(0, 50) + "...",
            messageCount: currentMessageCount,
          });

          // Store selected text as pending and mark that we're waiting for association
          if (selectedTextForUI && selectedTextForUI.length > 0) {
            setPendingSelectedText(selectedTextForUI);
            isPendingAssociationRef.current = true;
            previousMessageCountRef.current = currentMessageCount;

            log.debug("Stored pending selected text", {
              length: selectedTextForUI.length,
              previousCount: currentMessageCount,
              isPendingAssociation: true,
            });
          } else {
            // Clear any pending state if no selected text
            setPendingSelectedText("");
            isPendingAssociationRef.current = false;

            log.debug("No selected text to store");
          }

          // Process the message (existing logic unchanged)
          chatAnalysis.handleAnalyze(message);
        })
        .catch((error) => {
          log.warn("Failed to get selected text for UI, continuing without it", {
            error: error.message,
            messageCount: currentMessageCount,
          });

          // Clear any pending state on error
          setPendingSelectedText("");
          isPendingAssociationRef.current = false;

          // Continue with normal flow even if selected text fails
          chatAnalysis.handleAnalyze(message);
        });
      */

      // Selected text feature disabled - always clear state and proceed
      setPendingSelectedText("");
      isPendingAssociationRef.current = false;

      // Process the message. `opts` is set only by an add-in action; a typed
      // message passes undefined and the backend serves it exactly as it does
      // today.
      chatAnalysis.handleAnalyze(message, opts);
    }
  };

  // The Draft Certificate is the closing step of the Tier 3 workflow, so it is
  // available exactly where that workflow is: an entitled tenant on an agent
  // whose prompt was written for it. Which answer carries the offer is decided
  // in the message list.
  const { tier3Review: isTier3ReviewEnabled } = useEntitlements();
  const draftCertificateAvailable = isTier3ReviewEnabled && isTier3CapableAgent(currentAgent.id);
  const handleDraftCertificate = () => {
    log.info("Draft Certificate requested", { agentId: currentAgent.id });
    handleSendMessage(DRAFT_CERTIFICATE_PROMPT, { action: "draft_certificate" });
  };

  // Handle file selection from PlusMenuButton
  const handleFilesSelected = async (files: File[]) => {
    log.info("Files selected from PlusMenuButton", {
      count: files.length,
      fileNames: files.map((f) => f.name),
      agentId: currentAgent.id,
    });

    // Add files using the file upload hook
    await fileUpload.addFiles(files, "plus_menu");
  };

  /**
   * Handle prompt click from Prompt Coach component
   * Populates the chat input field without sending the message
   */
  const handlePromptClick = (promptText: string) => {
    log.info("Prompt Coach prompt clicked", {
      promptText,
      agentId: currentAgent.id,
    });

    // Use the ref to populate the input field
    if (chatInputRef.current) {
      chatInputRef.current.setInputValue(promptText);
      chatInputRef.current.focusInput();
    } else {
      log.warn("Chat input ref not available");
    }
  };

  /**
   * Handle fact-check button click
   * Sends the message content with DOUBLE_CHECK_SUFFIX as a new query
   */
  const handleFactCheck = (factCheckQuery: string) => {
    log.info("Fact-check initiated", {
      queryLength: factCheckQuery.length,
      agentId: currentAgent.id,
    });

    // Process the fact-check query through normal chat flow
    chatAnalysis.handleAnalyze(factCheckQuery);
  };

  /**
   * Message count tracking effect - the heart of our precise timing solution
   * This effect detects exactly when a new message is added and associates pending selected text
   */
  React.useEffect(() => {
    const currentMessageCount = chatAnalysis.currentSession.messages.length;
    const previousCount = previousMessageCountRef.current;

    // Check if we have a count increase and pending selected text to associate
    const hasCountIncreased = currentMessageCount > previousCount;
    const hasPendingText = pendingSelectedText && pendingSelectedText.length > 0;
    const isWaitingForAssociation = isPendingAssociationRef.current;

    log.debug("Message count tracking", {
      currentCount: currentMessageCount,
      previousCount,
      hasCountIncreased,
      hasPendingText,
      isWaitingForAssociation,
      pendingTextLength: pendingSelectedText.length,
    });

    if (hasCountIncreased && hasPendingText && isWaitingForAssociation) {
      // A new message was added! Find the user message that caused the count increase
      const userMessages = chatAnalysis.currentSession.messages.filter(
        (msg) => msg.role === "user"
      );

      // The new message should be the last user message (since count increased)
      const newUserMessage = userMessages[userMessages.length - 1];

      if (newUserMessage && !messageSelectedText.has(newUserMessage.id)) {
        // Associate pending selected text with the new message
        setMessageSelectedText((prev) => {
          const newMap = new Map(prev);
          newMap.set(newUserMessage.id, pendingSelectedText);

          log.info("Successfully associated selected text with new message", {
            messageId: newUserMessage.id,
            selectedTextLength: pendingSelectedText.length,
            messageContent: newUserMessage.content.substring(0, 50) + "...",
            previousCount,
            currentCount: currentMessageCount,
          });

          return newMap;
        });

        // Clear pending state after successful association
        setPendingSelectedText("");
        isPendingAssociationRef.current = false;

        log.debug("Cleared pending selected text after successful association");
      } else if (!newUserMessage) {
        log.warn("Count increased but no new user message found", {
          currentCount: currentMessageCount,
          previousCount,
          userMessageCount: userMessages.length,
        });
      } else {
        log.warn("New user message already has selected text associated", {
          messageId: newUserMessage.id,
          hasExistingText: messageSelectedText.has(newUserMessage.id),
        });
      }
    }

    // Update our tracking reference for next comparison
    previousMessageCountRef.current = currentMessageCount;
  }, [
    chatAnalysis.currentSession.messages.length, // This is what we're watching for changes
    pendingSelectedText, // Include this to react to pending text changes
    messageSelectedText, // Include this to avoid stale closure issues
  ]);

  // Check if this is a new conversation (no messages yet)
  const isNewConversation = chatAnalysis.currentSession.messages.length === 0;

  // Create dynamic placeholder text based on conversation state and agent
  const placeholderText = React.useMemo(() => {
    // Use agent-specific placeholder if available
    if (currentAgentFeatures.placeholderOverride) {
      return currentAgentFeatures.placeholderOverride;
    }

    if (isNewConversation) {
      // First-time user experience: provide helpful guidance and point to suggestions
      return "Not sure what to ask, above are some suggestions to get you started";
    } else {
      // Ongoing conversation: simple, encouraging prompt for continued interaction
      return "Ask me anything";
    }
  }, [isNewConversation, currentAgentFeatures.placeholderOverride]);

  // Log placeholder changes for development debugging
  React.useEffect(() => {
    log.debug("Placeholder text updated", {
      isNewConversation,
      placeholderText,
      messageCount: chatAnalysis.currentSession.messages.length,
      hasAgentOverride: !!currentAgentFeatures.placeholderOverride,
    });
  }, [
    placeholderText,
    isNewConversation,
    chatAnalysis.currentSession.messages.length,
    currentAgentFeatures.placeholderOverride,
  ]);

  // Log current state for debugging
  React.useEffect(() => {
    log.debug("StreamingChatPanel state update", {
      isGenerating: chatAnalysis.isGenerating,
      messageCount: chatAnalysis.currentSession.messages.length,
      uploadedFiles: fileUpload.files.length,
      agentAllowsFiles: currentAgentFeatures.allowFileUpload,
    });
  }, [
    chatAnalysis.isGenerating,
    chatAnalysis.currentSession.messages.length,
    fileUpload.files.length,
    currentAgentFeatures.allowFileUpload,
  ]);

  return (
    <div
      ref={containerRef}
      style={{
        ...modernChatStyles.chatContainer,
        display: "flex",
        flexDirection: "column",
        flex: 1,
        overflow: "hidden",
      }}
    >
      {/* Top actions area with error message */}
      <div
        style={{
          padding: "0px 0px",
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "8px",
          flexShrink: 0,
        }}
      >
        {/* Error message */}
        {error && (
          <div style={{ flex: 1, minWidth: 0 }}>
            <ErrorMessage error={error} onDismiss={handleDismissError} />
          </div>
        )}
      </div>

      {/* Messages area (scrollable) */}
      <div
        ref={chatContainerRef}
        className="chat-messages-container"
        style={{
          ...modernChatStyles.messagesContainer,
          flex: 1,
          // Only have overflow on this outer container, not in the inner component
          overflowY:
            containerHeight > 0 && chatAnalysis.currentSession.messages.length > 0
              ? "auto"
              : "hidden",
          overflowX: "hidden",
        }}
      >
        {/* Conditional rendering for messages vs centered empty state */}
        {!showCenteredLayout &&
        (chatAnalysis.currentSession.messages.length > 0 || chatAnalysis.isGenerating) ? (
          <ChatMessages
            messages={chatAnalysis.currentSession.messages}
            isStreaming={chatAnalysis.isGenerating}
            messageSelectedText={messageSelectedText}
            onPromptClick={handlePromptClick}
            onFactCheck={handleFactCheck}
            onPlaybookSelected={chatAnalysis.handlePlaybookSelected}
            onDraftCertificate={draftCertificateAvailable ? handleDraftCertificate : undefined}
            scrollContainerRef={chatContainerRef}
            // REMOVED: conversation-level thinking props - now handled per-message
          />
        ) : (
          // Empty state - centered layout with greeting and input
          <div
            style={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              padding: "20px",
              gap: "24px",
              opacity: isTransitioning ? 0 : 1,
              transition: "opacity 0.2s ease-out",
            }}
          >
            {/* Greeting */}
            <div
              style={{
                textAlign: "center",
                background: "linear-gradient(135deg, #0066cc, #75B6FF)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                fontSize: "48px",
                fontWeight: 400,
                fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
                letterSpacing: "-0.5px",
              }}
            >
              {getPersonalizedGreeting(userInfo?.name)}
            </div>

            {/* Centered input and controls */}
            <div
              style={{
                width: "100%",
                maxWidth: "600px",
                display: "flex",
                flexDirection: "column",
                gap: "12px",
              }}
            >
              {/* Prompt Suggestion Carousel */}
              {/* {currentAgentFeatures.showPromptSuggestions && (
                <PromptSuggestionCarousel onSuggestionClick={handleSuggestionClick} />
              )} */}

              {/* Document Referenced heading */}
              {(currentAgent.id === "contract-qa" ||
                currentAgent.id === "drafting-assistant" ||
                currentAgent.id === "ccm-space") &&
                fileUpload.files.length > 0 && (
                  <h3
                    style={{
                      fontSize: "16px",
                      fontWeight: 600,
                      color: "#000",
                      margin: "0 8px 0 8px",
                      fontFamily: "'Segoe UI', sans-serif",
                    }}
                  >
                    Document Referenced
                  </h3>
                )}

              {/* File Upload Carousel */}
              {currentAgentFeatures.allowFileUpload && (
                <FileUploadCarousel
                  files={fileUpload.files}
                  onRemoveFile={fileUpload.removeFile}
                  error={fileUpload.error || undefined}
                />
              )}

              {/* Input */}
              <ModernChatInput
                ref={chatInputRef}
                onSendMessage={handleSendMessage}
                isLoading={chatAnalysis.busy || chatAnalysis.isGenerating}
                placeholder={placeholderText}
                onInsert={handleInsert}
                canInsert={!!(chatAnalysis.lastResponseText || chatAnalysis.directResponseText)}
                isInserting={documentInsertion.busy}
                showClearChat={showClearButton}
                onClearChat={chatAnalysis.handleClearChat}
                containerHeight={containerHeight}
                onFilesSelected={handleFilesSelected}
                onRemoveFile={fileUpload.removeFile}
              />

              {/* Quick Start Buttons */}
              <QuickStartButtons
                agentId={currentAgent.id}
                onPromptSelect={handleSendMessage}
                isVisible={
                  !chatAnalysis.currentSession.messages ||
                  chatAnalysis.currentSession.messages.length === 0
                }
              />
            </div>
          </div>
        )}
      </div>

      {/* Bottom area with prompts, files, and input - ONLY shown when messages exist */}
      {!showCenteredLayout &&
        (chatAnalysis.currentSession.messages.length > 0 || chatAnalysis.isGenerating) && (
          <div
            style={{
              marginTop: "auto",
              display: "flex",
              flexDirection: "column",
              gap: "8px",
            }}
          >
            {/* Prompt Suggestion Carousel - Show based on agent configuration */}
            {/* {isNewConversation && currentAgentFeatures.showPromptSuggestions && (
            <PromptSuggestionCarousel onSuggestionClick={handleSuggestionClick} />
          )} */}

            {/* Document Referenced heading - show for contract-qa and drafting-assistant agents with files */}
            {currentAgent.id === "contract-qa" ||
              currentAgent.id === "drafting-assistant" ||
              (currentAgent.id === "ccm-space" && fileUpload.files.length > 0 && (
                <h3
                  style={{
                    fontSize: "16px",
                    fontWeight: 600,
                    color: "#000",
                    margin: "0 8px 0 8px", // the margins are as follows: first value is top, second is right, third is bottom, fourth is left
                    fontFamily: "'Segoe UI', sans-serif",
                  }}
                >
                  Document Referenced
                </h3>
              ))}

            {/* File Upload Carousel - only shown for agents that support file upload */}
            {currentAgentFeatures.allowFileUpload && (
              <FileUploadCarousel
                files={fileUpload.files}
                onRemoveFile={fileUpload.removeFile}
                error={fileUpload.error || undefined}
              />
            )}

            {/* Bottom input area with modern design */}
            <ModernChatInput
              ref={chatInputRef}
              onSendMessage={handleSendMessage}
              isLoading={chatAnalysis.busy || chatAnalysis.isGenerating}
              placeholder={placeholderText}
              onInsert={handleInsert}
              canInsert={!!(chatAnalysis.lastResponseText || chatAnalysis.directResponseText)}
              isInserting={documentInsertion.busy}
              showClearChat={showClearButton}
              onClearChat={chatAnalysis.handleClearChat}
              containerHeight={containerHeight}
              onFilesSelected={handleFilesSelected}
              onRemoveFile={fileUpload.removeFile}
            />

            {/* Disclaimer text - always show */}
            <div
              style={{
                fontSize: "13px",
                color: "#666",
                textAlign: "center",
                padding: "0px 4px",
                fontFamily: "'Segoe UI', sans-serif",
              }}
            >
              Contract Space can make mistakes. Please check important info.
            </div>
          </div>
        )}
    </div>
  );
};

export default StreamingChatPanel;
